import { useEffect, useState } from "react";

import { useHandleBusinessService } from "0.Mine/1.Hooks/business/services";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { BusinessService } from "0.Mine/3.Interfaces/Services";

import { BusinessFormProps } from "./BusinessFormProps";

export const useServiceManagerLogic = (business: BusinessFormProps["business"]) => {
  const [treatments, setTreatments] = useState<BusinessService[]>([]);
  const { createService, getAllServices, updateService } = useHandleBusinessService();
  const { session } = useBusinessContext();
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (business) {
        const data = await getAllServices(business.id);
        // Ensure treatments is always an array
        setTreatments(Array.isArray(data) ? data : []);
      }
    };
    fetchData();
  }, [business]);

  const handleAddTreatment = async (treatment: BusinessService) => {
    try {
      //First we check if the name of the service already exists. if it exists, we should display an icon rejecting the addition of the service
      if (treatments.some((t) => t.name === treatment.name)) {
        setOpenSnackBar(true);
        setSnackBarMessage("Service already exists");
        return;
      }
      //Then we check if the price is a positive number. After which we should display an icon rejecting the addition of the service
      if (treatment.price <= 0) {
        setOpenSnackBar(true);
        setSnackBarMessage("Price must be a positive number");
        return;
      }

      // First add the new treatment to our local array
      const updatedTreatments = [...treatments, treatment];

      // Send the complete updated array to the backend
      await createService(business.id, treatment);

      // Update the local state after successful API call
      setTreatments(updatedTreatments);
    } catch (error) {
      console.error("Failed to add treatment:", error);
    }
  };

  const handleDeleteTreatment = (index: number) => {
    console.log("Deleting treatment at index:", index);
    setTreatments(treatments.filter((_, i) => i !== index));
  };

  const handleReorderTreatments = (newTreatments: BusinessService[]) => {
    setTreatments(newTreatments);
  };

  // Add the update treatment handler
  const handleUpdateTreatment = async (updatedTreatment: BusinessService) => {
    try {
      if (!session || !session?.selectedBusiness) {
        setSnackBarMessage("No business selected");
        setOpenSnackBar(true);
        return;
      }

      // Create a temporary version with the update
      const updatedTreatments = treatments.map((treatment) =>
        treatment._id === updatedTreatment._id ? updatedTreatment : treatment
      );

      // Update state immediately for better UX
      setTreatments(updatedTreatments);

      // Then call API to persist changes
      const response = await updateService(session.selectedBusiness.id, updatedTreatment);

      if (!response.success) {
        // If API call fails, revert to original state
        setTreatments(treatments);
        setSnackBarMessage(response.message || "Failed to update service");
        setOpenSnackBar(true);
      }
    } catch (error) {
      console.error("Error updating treatment:", error);
      setSnackBarMessage("An error occurred while updating the service");
      setOpenSnackBar(true);
      // Revert to original state
      setTreatments([...treatments]);
    }
  };

  const closeSnackBar = () => {
    setOpenSnackBar(false);
  };

  return {
    session,
    treatments,
    closeSnackBar,
    openSnackBar,
    snackBarMessage,
    handleAddTreatment,
    handleDeleteTreatment,
    handleReorderTreatments,
    handleUpdateTreatment, // Add this
    createService,
  };
};
